// router/index.js - Configuration complète avec store Pinia
import {
    createWebHistory,
    createRouter
} from 'vue-router'
import {
    useAuthStore
} from '@/stores/auth.js'
import {
    useLoaderStore
} from '@/stores/loader.js'
import AppLayout from '@/components/layout/AppLayout.vue'

// Pages publiques (statiques)
import LoginPage from '../Pages/LoginPage.vue'
import ForgotPasswordPage from '../Pages/ForgotPasswordPage.vue'
import ResetPasswordPage from '../Pages/ResetPasswordPage.vue'

// Composant de fallback minimal — pas de dépendances
import {
    h
} from 'vue'
const PlaceholderPage = {
    setup() {
        return () => h('div', {
            class: 'min-h-screen flex items-center justify-center bg-offwhite'
        }, [
            h('div', {
                class: 'text-center'
            }, [
                h('i', {
                    class: 'pi pi-cog text-teal text-4xl mb-4 block'
                }),
                h('h1', {
                    class: 'text-xl font-bold text-navy mb-2'
                }, 'Page en construction'),
                h('p', {
                    class: 'text-navy/50 text-sm'
                }, 'Cette page sera bientôt disponible')
            ])
        ])
    }
}

// Import glob de toutes les pages pour que Vite puisse les analyser statiquement
const pages =
    import.meta.glob('../Pages/**/*.vue')

function lazy(path) {
    const key = '../Pages/' + path
    return function() {
        const importer = pages[key]
        if (!importer) {
            console.error('❌ Page introuvable:', key)
            return Promise.resolve(PlaceholderPage)
        }
        return importer().catch(function(err) {
            console.error('❌ Erreur import page:', key, err)
            return PlaceholderPage
        })
    }
}

const authenticatedChildren = [
    // ── NOTIFICATIONS (accessible à tous) ──────────────────────────────────────
    {
        path: '/notifications',
        component: lazy('Notifications/ListeNotifications.vue'),
        name: 'notifications',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN', 'RESPONSABLE_ACHAT', 'RESPONSABLE_LABO', 'TECHNICIEN', 'CONTROLEUR_GESTION', 'CORRESPONDANT_ACHAT', 'RESPONSABLE_SERVICE', 'DAF', 'COMPTABLE', 'DG'],
            title: 'Notifications'
        }
    },

    // ── ADMIN ──────────────────────────────────────────────────────────────────
    {
        path: '/admin/dashboard',
        component: lazy('Dashboard.vue'),
        name: 'admin-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Dashboard Admin'
        }
    },
    {
        path: '/admin/utilisateurs',
        component: lazy('ListeUtilisateurs.vue'),
        name: 'admin-users',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des utilisateurs'
        }
    },
    {
        path: '/admin/produits',
        component: lazy('ListeProduits.vue'),
        name: 'admin-products',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des produits'
        }
    },
    {
        path: '/admin/laboratoires',
        component: lazy('ListeLaboratoires.vue'),
        name: 'admin-labs',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des laboratoires'
        }
    },
    {
        path: '/admin/laboratoires/:id',
        component: lazy('DetailLaboratoire.vue'),
        name: 'admin-lab-detail',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Détail laboratoire'
        }
    },
    {
        path: '/admin/laboratoires/:laboratoryId/stocks',
        component: lazy('Labo/InventaireLaboratoire.vue'),
        name: 'admin-lab-stocks',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Stocks du laboratoire'
        }
    },
    {
        path: '/admin/types-laboratoires',
        component: lazy('Admin/GestionTypesLaboratoires.vue'),
        name: 'admin-lab-types',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Types de laboratoires'
        }
    },
    {
        path: '/admin/unites',
        component: lazy('Admin/GestionUnites.vue'),
        name: 'admin-units',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des unités'
        }
    },
    {
        path: '/admin/departements',
        component: lazy('Admin/GestionDepartements.vue'),
        name: 'admin-departments',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des départements'
        }
    },
    {
        path: '/admin/departements/:id',
        component: lazy('Admin/DetailDepartement.vue'),
        name: 'admin-department-detail',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Détail département'
        }
    },
    {
        path: '/admin/historique',
        component: lazy('Historique.vue'),
        name: 'admin-history',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Historique global'
        }
    },
    {
        path: '/admin/logs',
        component: lazy('GestionLogs.vue'),
        name: 'admin-logs',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: "Logs d'audit"
        }
    },
    {
        path: '/admin/logs/:id',
        component: lazy('DetailLog.vue'),
        name: 'admin-log-detail',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Détail du log'
        }
    },
    {
        path: '/admin/rapports',
        component: lazy('Rapports.vue'),
        name: 'admin-reports',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Rapports globaux'
        }
    },
    {
        path: '/admin/alertes',
        component: lazy('Alertes/GestionAlertes.vue'),
        name: 'admin-alerts',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des alertes'
        }
    },
    {
        path: '/admin/demandes',
        component: lazy('Achat/AlertesDemandes.vue'),
        name: 'admin-demandes',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des demandes'
        }
    },
    {
        path: '/admin/mouvements',
        component: lazy('Stock/GestionMouvements.vue'),
        name: 'admin-movements',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Mouvements de stock'
        }
    },
    {
        path: '/admin/equipements',
        component: lazy('Equipements/GestionEquipements.vue'),
        name: 'admin-equipments',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Gestion des équipements'
        }
    },
    {
        path: '/admin/maintenances',
        component: lazy('Admin/GestionMaintenances.vue'),
        name: 'admin-maintenances',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Maintenances équipements'
        }
    },
    {
        path: '/admin/maintenances-infrastructure',
        component: lazy('Admin/GestionMaintenancesInfrastructure.vue'),
        name: 'admin-maintenances-infrastructure',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Maintenances infrastructure'
        }
    },
    {
        path: '/admin/jobs',
        component: lazy('Achat/GestionJobs.vue'),
        name: 'admin-jobs',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Jobs Planifiés'
        }
    },
    {
        path: '/admin/configuration-alertes',
        component: lazy('Achat/ConfigurationAlertes.vue'),
        name: 'admin-config-alerts',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Configuration Alertes'
        }
    },
    {
        path: '/admin/analysis-config',
        component: lazy('Admin/GestionAnalysisConfig.vue'),
        name: 'admin-analysis-config',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Config. Analyses'
        }
    },
    {
        path: '/admin/calcul-cout-analyse',
        component: lazy('Admin/CalculCoutAnalyse.vue'),
        name: 'admin-calcul-cout-analyse',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: "Calcul Coût Analyse"
        }
    },
    {
        path: '/admin/parametres',
        component: lazy('Parametres.vue'),
        name: 'admin-settings',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Paramètres système'
        }
    },
    {
        path: '/admin/profile',
        component: lazy('ProfilePage.vue'),
        name: 'admin-profile',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Mon profil'
        }
    },
    {
        path: '/admin/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'admin-change-password',
        meta: {
            requiresAuth: true,
            roles: ['ADMIN'],
            title: 'Changer le mot de passe'
        }
    },

    // ── RESPONSABLE ACHAT ──────────────────────────────────────────────────────
    {
        path: '/achat/dashboard',
        component: lazy('Achat/DashboardResponsableAchat.vue'),
        name: 'achat-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Dashboard Achat'
        }
    },
    {
        path: '/achat/stock-general',
        component: lazy('Achat/GestionStockGeneral.vue'),
        name: 'achat-stock-general',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Stock Général'
        }
    },
    {
        path: '/achat/stock-yaou',
        component: lazy('Achat/GestionStockYaou.vue'),
        name: 'achat-stock-yaou',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Dépôt YAOU'
        }
    },
    {
        path: '/achat/vue-ensemble-stocks',
        component: lazy('Achat/VueEnsembleStocks.vue'),
        name: 'achat-vue-ensemble',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Vue d\'ensemble des stocks'
        }
    },
    {
        path: '/achat/commandes',
        component: lazy('Achat/ListeCommandes.vue'),
        name: 'achat-orders',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des commandes'
        }
    },
    {
        path: '/achat/commandes/:id',
        component: lazy('Achat/DetailCommande.vue'),
        name: 'achat-order-detail',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Détail commande'
        }
    },
    {
        path: '/achat/produits',
        component: lazy('ListeProduits.vue'),
        name: 'achat-products',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des produits'
        }
    },
    {
        path: '/achat/fournisseurs',
        component: lazy('Achat/ListeFournisseurs.vue'),
        name: 'achat-suppliers',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des fournisseurs'
        }
    },
    {
        path: '/achat/laboratoires',
        component: lazy('ListeLaboratoires.vue'),
        name: 'achat-laboratories',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des laboratoires'
        }
    },
    {
        path: '/achat/laboratoires/:id',
        component: lazy('DetailLaboratoire.vue'),
        name: 'achat-lab-detail',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Détail laboratoire'
        }
    },
    {
        path: '/achat/receptions',
        component: lazy('Achat/ListeReceptions.vue'),
        name: 'achat-receptions',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Réceptions'
        }
    },
    {
        path: '/achat/receptions/:id',
        component: lazy('Achat/DetailReception.vue'),
        name: 'achat-reception-detail',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Détail réception'
        }
    },
    {
        path: '/achat/dispatch',
        component: lazy('Achat/SystemeDispatch.vue'),
        name: 'achat-dispatch',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Système de dispatch'
        }
    },
    {
        path: '/achat/dispatch/:id',
        component: lazy('Achat/DispatchDetails.vue'),
        name: 'achat-dispatch-detail',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Détail dispatch'
        }
    },
    {
        path: '/achat/categories',
        component: lazy('Achat/GestionCategories.vue'),
        name: 'achat-categories',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Catégories & Sous-catégories'
        }
    },
    {
        path: '/achat/jobs',
        component: lazy('Achat/GestionJobs.vue'),
        name: 'achat-jobs',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des Jobs'
        }
    },
    {
        path: '/achat/configuration-alertes',
        component: lazy('Achat/ConfigurationAlertes.vue'),
        name: 'achat-config-alerts',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Configuration des Alertes'
        }
    },
    {
        path: '/achat/proformas',
        component: lazy('Achat/GestionProformas.vue'),
        name: 'achat-proformas',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des proformas'
        }
    },
    {
        path: '/achat/proformas/nouvelle',
        component: lazy('Achat/NouvelleProforma.vue'),
        name: 'achat-new-proforma',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Nouvelle proforma'
        }
    },
    {
        path: '/achat/proformas/:id',
        component: lazy('Achat/DetailProforma.vue'),
        name: 'achat-proforma-detail',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Détail proforma'
        }
    },
    {
        path: '/achat/budget',
        component: lazy('Achat/GestionBudgetaire.vue'),
        name: 'achat-budget',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion budgétaire'
        }
    },
    {
        path: '/achat/alertes',
        component: lazy('Alertes/GestionAlertes.vue'),
        name: 'achat-alerts',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des alertes'
        }
    },
    {
        path: '/achat/demandes',
        component: lazy('Achat/AlertesDemandes.vue'),
        name: 'achat-demandes',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des demandes'
        }
    },
    {
        path: '/achat/demandes-generales',
        component: lazy('Achat/DemandesGenerales.vue'),
        name: 'achat-general-requests',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Demandes des Services'
        }
    },
    {
        path: '/achat/mouvements',
        component: lazy('Stock/GestionMouvements.vue'),
        name: 'achat-movements',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Mouvements de stock'
        }
    },
    {
        path: '/achat/equipements',
        component: lazy('Equipements/GestionEquipements.vue'),
        name: 'achat-equipments',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Gestion des équipements'
        }
    },
    {
        path: '/achat/parametres',
        component: lazy('Parametres.vue'),
        name: 'achat-settings',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Paramètres Achat'
        }
    },
    {
        path: '/achat/profile',
        component: lazy('ProfilePage.vue'),
        name: 'achat-profile',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Mon profil'
        }
    },
    {
        path: '/achat/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'achat-change-password',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_ACHAT'],
            title: 'Changer le mot de passe'
        }
    },

    // ── RESPONSABLE LABORATOIRE ────────────────────────────────────────────────
    {
        path: '/labo/dashboard',
        component: lazy('Labo/DashboardResponsableLabo.vue'),
        name: 'labo-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Dashboard Laboratoire'
        }
    },
    {
        path: '/labo/stock/inventaire',
        component: lazy('Labo/InventaireLaboratoire.vue'),
        name: 'labo-inventory',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Inventaire laboratoire'
        }
    },
    {
        path: '/labo/produits',
        component: lazy('Labo/ProduitsLaboratoire.vue'),
        name: 'labo-products',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Produits du laboratoire'
        }
    },
    {
        path: '/labo/sorties',
        component: lazy('Labo/ListeSortiesLaboratoire.vue'),
        name: 'labo-exits',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Validation sorties'
        }
    },
    {
        path: '/labo/demandes',
        component: lazy('Labo/GestionDemandesEquipe.vue'),
        name: 'labo-team-requests',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Demandes équipe'
        }
    },
    {
        path: '/labo/demandes/:id/suivi',
        component: lazy('Labo/SuiviDemande.vue'),
        name: 'labo-request-tracking',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Suivi de la demande'
        }
    },
    {
        path: '/labo/transferts',
        component: lazy('Labo/TransfertsInterLaboratoires.vue'),
        name: 'labo-transfers',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Gestion transferts'
        }
    },
    {
        path: '/labo/equipe',
        component: lazy('Labo/GestionEquipeLaboratoire.vue'),
        name: 'labo-team',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Gestion équipe'
        }
    },
    {
        path: '/labo/echantillons',
        component: lazy('Labo/ListeEchantillons.vue'),
        name: 'labo-samples',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Gestion échantillons'
        }
    },
    {
        path: '/labo/rapports',
        component: lazy('Labo/RapportsLaboratoire.vue'),
        name: 'labo-reports',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Rapports laboratoire'
        }
    },
    {
        path: '/labo/alertes',
        component: lazy('Alertes/GestionAlertes.vue'),
        name: 'labo-alerts',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Gestion des alertes'
        }
    },
    {
        path: '/labo/parametres',
        component: lazy('Parametres.vue'),
        name: 'labo-settings',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Paramètres Laboratoire'
        }
    },
    {
        path: '/labo/profile',
        component: lazy('ProfilePage.vue'),
        name: 'labo-profile',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Mon profil'
        }
    },
    {
        path: '/labo/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'labo-change-password',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_LABO', 'CORRESPONDANT_ACHAT'],
            title: 'Changer le mot de passe'
        }
    },

    // ── TECHNICIEN ─────────────────────────────────────────────────────────────
    {
        path: '/technicien/dashboard',
        component: lazy('Technicien/DashboardTechnicien.vue'),
        name: 'technicien-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Mon Dashboard'
        }
    },
    {
        path: '/technicien/stock',
        component: lazy('Technicien/ConsultationStock.vue'),
        name: 'technicien-stock',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Consultation stock'
        }
    },
    {
        path: '/technicien/sorties',
        component: lazy('Technicien/SaisieSorties.vue'),
        name: 'technicien-exits',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Saisie des sorties'
        }
    },
    {
        path: '/technicien/demandes',
        component: lazy('Technicien/MesDemandes.vue'),
        name: 'technicien-requests',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Mes demandes'
        }
    },
    {
        path: '/technicien/analyses',
        component: lazy('Technicien/GestionAnalyses.vue'),
        name: 'technicien-analyses',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Gestion des analyses'
        }
    },
    {
        path: '/technicien/parametres',
        component: lazy('Parametres.vue'),
        name: 'technicien-settings',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Paramètres Technicien'
        }
    },
    {
        path: '/technicien/profile',
        component: lazy('ProfilePage.vue'),
        name: 'technicien-profile',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Mon profil'
        }
    },
    {
        path: '/technicien/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'technicien-change-password',
        meta: {
            requiresAuth: true,
            roles: ['TECHNICIEN'],
            title: 'Changer le mot de passe'
        }
    },

    // ── DEV ────────────────────────────────────────────────────────────────────
    {
        path: '/dev/components',
        component: lazy('Dev/ComponentsDemo.vue'),
        name: 'dev-components',
        meta: {
            public: true
        },
        beforeEnter: function(to, from, next) {
            if (
                import.meta.env.PROD) {
                next('/')
            } else {
                next()
            }
        }
    },

    // ── DAF ────────────────────────────────────────────────────────────────────
    {
        path: '/daf/dashboard',
        component: lazy('DAF/DashboardDAF.vue'),
        name: 'daf-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['DAF'],
            title: 'Dashboard DAF'
        }
    },
    {
        path: '/daf/commandes',
        component: lazy('DAF/CommandesDAF.vue'),
        name: 'daf-orders',
        meta: {
            requiresAuth: true,
            roles: ['DAF'],
            title: 'Commandes'
        }
    },
    {
        path: '/daf/commandes/:id',
        component: lazy('DAF/DetailCommandeDAF.vue'),
        name: 'daf-order-detail',
        meta: {
            requiresAuth: true,
            roles: ['DAF'],
            title: 'Détail commande'
        }
    },
    {
        path: '/daf/demandes',
        component: lazy('DAF/DemandesDAF.vue'),
        name: 'daf-requests',
        meta: {
            requiresAuth: true,
            roles: ['DAF'],
            title: 'Demandes réappro.'
        }
    },
    {
        path: '/daf/profile',
        component: lazy('ProfilePage.vue'),
        name: 'daf-profile',
        meta: {
            requiresAuth: true,
            roles: ['DAF'],
            title: 'Mon profil'
        }
    },
    {
        path: '/daf/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'daf-change-password',
        meta: {
            requiresAuth: true,
            roles: ['DAF'],
            title: 'Changer le mot de passe'
        }
    },

    // ── COMPTABLE ──────────────────────────────────────────────────────────────
    {
        path: '/compta/dashboard',
        component: lazy('Comptabilite/DashboardComptable.vue'),
        name: 'compta-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['COMPTABLE'],
            title: 'Dashboard Comptabilite'
        }
    },
    {
        path: '/compta/commandes',
        component: lazy('Comptabilite/CommandesComptable.vue'),
        name: 'compta-orders',
        meta: {
            requiresAuth: true,
            roles: ['COMPTABLE'],
            title: 'Commandes'
        }
    },
    {
        path: '/compta/commandes/:id',
        component: lazy('Comptabilite/DetailCommandeComptable.vue'),
        name: 'compta-order-detail',
        meta: {
            requiresAuth: true,
            roles: ['COMPTABLE'],
            title: 'Detail commande'
        }
    },
    {
        path: '/compta/demandes',
        component: lazy('Comptabilite/DemandesComptable.vue'),
        name: 'compta-requests',
        meta: {
            requiresAuth: true,
            roles: ['COMPTABLE'],
            title: 'Demandes'
        }
    },
    {
        path: '/compta/profile',
        component: lazy('ProfilePage.vue'),
        name: 'compta-profile',
        meta: {
            requiresAuth: true,
            roles: ['COMPTABLE'],
            title: 'Mon profil'
        }
    },
    {
        path: '/compta/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'compta-change-password',
        meta: {
            requiresAuth: true,
            roles: ['COMPTABLE'],
            title: 'Changer le mot de passe'
        }
    },

    // ── DG ─────────────────────────────────────────────────────────────────────
    {
        path: '/dg/dashboard',
        component: lazy('DG/DashboardDG.vue'),
        name: 'dg-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['DG'],
            title: 'Dashboard DG'
        }
    },
    {
        path: '/dg/commandes',
        component: lazy('DG/CommandesDG.vue'),
        name: 'dg-orders',
        meta: {
            requiresAuth: true,
            roles: ['DG'],
            title: 'Commandes'
        }
    },
    {
        path: '/dg/commandes/:id',
        component: lazy('DG/DetailCommandeDG.vue'),
        name: 'dg-order-detail',
        meta: {
            requiresAuth: true,
            roles: ['DG'],
            title: 'Detail commande'
        }
    },
    {
        path: '/dg/demandes',
        component: lazy('DG/DemandesDG.vue'),
        name: 'dg-requests',
        meta: {
            requiresAuth: true,
            roles: ['DG'],
            title: 'Demandes'
        }
    },
    {
        path: '/dg/profile',
        component: lazy('ProfilePage.vue'),
        name: 'dg-profile',
        meta: {
            requiresAuth: true,
            roles: ['DG'],
            title: 'Mon profil'
        }
    },
    {
        path: '/dg/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'dg-change-password',
        meta: {
            requiresAuth: true,
            roles: ['DG'],
            title: 'Changer le mot de passe'
        }
    },

    // ── RESPONSABLE SERVICE (département type AUTRE) ───────────────────────────
    {
        path: '/service/dashboard',
        component: lazy('Service/DashboardService.vue'),
        name: 'service-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Mon Tableau de Bord'
        }
    },
    {
        path: '/service/demandes',
        component: lazy('Service/MesDemandesService.vue'),
        name: 'service-requests',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Mes Demandes'
        }
    }, {
        path: '/service/demandes/nouvelle',
        component: lazy('Service/NouvelleDemande.vue'),
        name: 'service-new-request',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Nouvelle Demande'
        }
    }, {
        path: '/service/demandes/:id',
        component: lazy('Service/DetailDemandeService.vue'),
        name: 'service-request-detail',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Détail Demande'
        }
    }, {
        path: '/service/demandes-generales',
        component: lazy('Service/MesDemandesGenerales.vue'),
        name: 'service-general-requests',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Mes Demandes Générales'
        }
    }, {
        path: '/service/demandes-generales/nouvelle',
        component: lazy('Service/NouvelleDemandGenerale.vue'),
        name: 'service-new-general-request',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Nouvelle Demande Générale'
        }
    }, {
        path: '/service/profile',
        component: lazy('ProfilePage.vue'),
        name: 'service-profile',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Mon profil'
        }
    }, {
        path: '/service/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'service-change-password',
        meta: {
            requiresAuth: true,
            roles: ['RESPONSABLE_SERVICE'],
            title: 'Changer le mot de passe'
        }
    },

    // ── CONTRÔLEUR DE GESTION ──────────────────────────────────────────────────
    {
        path: '/controle/dashboard',
        component: lazy('ControleGestion/DashboardControleGestion.vue'),
        name: 'controle-dashboard',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Dashboard Contrôle de Gestion'
        }
    }, {
        path: '/controle/stocks',
        component: lazy('ControleGestion/AnalyseStocks.vue'),
        name: 'controle-stocks',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Analyse des Stocks'
        }
    }, {
        path: '/controle/budgets',
        component: lazy('ControleGestion/ControleBudgetaire.vue'),
        name: 'controle-budgets',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Contrôle Budgétaire'
        }
    }, {
        path: '/controle/kpis',
        component: lazy('ControleGestion/TableauBordKPI.vue'),
        name: 'controle-kpis',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Tableau de Bord KPIs'
        }
    }, {
        path: '/controle/rapports',
        component: lazy('Rapports.vue'),
        name: 'controle-rapports',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Rapports'
        }
    }, {
        path: '/controle/profile',
        component: lazy('ProfilePage.vue'),
        name: 'controle-profile',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Mon profil'
        }
    }, {
        path: '/controle/change-password',
        component: lazy('ChangePasswordPage.vue'),
        name: 'controle-change-password',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Changer le mot de passe'
        }
    }, {
        path: '/controle/parametres',
        component: lazy('Parametres.vue'),
        name: 'controle-parametres',
        meta: {
            requiresAuth: true,
            roles: ['CONTROLEUR_GESTION', 'ADMIN'],
            title: 'Paramètres'
        }
    },
]

const routes = [
    // ─── Routes publiques ──────────────────────────────────────────────────────
    {
        path: '/',
        component: LoginPage,
        meta: {
            public: true,
            title: 'Connexion'
        }
    },
    {
        path: '/login',
        component: LoginPage,
        meta: {
            public: true,
            title: 'Connexion'
        }
    },
    {
        path: '/forgot-password',
        component: ForgotPasswordPage,
        meta: {
            public: true,
            title: 'Mot de passe oublié'
        }
    },
    {
        path: '/reset-password',
        component: ResetPasswordPage,
        meta: {
            public: true,
            title: 'Réinitialiser le mot de passe'
        }
    },

    // ─── Routes authentifiées (AppLayout comme parent) ────────────────────────
    {
        path: '/',
        component: AppLayout,
        children: authenticatedChildren
    },

    // ─── Redirections et aliases ───────────────────────────────────────────────
    {
        path: '/responsable-achat/dashboard',
        redirect: '/achat/dashboard'
    },
    {
        path: '/responsable-achat/stock-general',
        redirect: '/achat/stock-general'
    },
    {
        path: '/responsable-achat/commandes',
        redirect: '/achat/commandes'
    },
    {
        path: '/responsable-achat/dispatches',
        redirect: '/achat/dispatch'
    },
    {
        path: '/responsable-achat/restocks',
        redirect: '/achat/demandes'
    },
    {
        path: '/responsable-achat/proformas',
        redirect: '/achat/proformas'
    },
    {
        path: '/responsable-achat/fournisseurs',
        redirect: '/achat/fournisseurs'
    },
    {
        path: '/responsable-achat/receptions',
        redirect: '/achat/receptions'
    },
    {
        path: '/responsable-achat/alertes',
        redirect: '/achat/alertes'
    },
    {
        path: '/responsable-achat/demandes',
        redirect: '/achat/demandes'
    },
    {
        path: '/responsable-achat/rapports',
        redirect: '/achat/mouvements'
    },
    {
        path: '/responsable-achat/statistiques',
        redirect: '/achat/budget'
    },
    {
        path: '/labo/stock',
        redirect: '/labo/stock/inventaire'
    },
    {
        path: '/admin',
        redirect: '/admin/dashboard'
    },
    {
        path: '/controle',
        redirect: '/controle/dashboard'
    },
    {
        path: '/achat',
        redirect: '/achat/dashboard'
    },
    {
        path: '/labo',
        redirect: '/labo/dashboard'
    },
    {
        path: '/technicien',
        redirect: '/technicien/dashboard'
    },
    {
        path: '/service',
        redirect: '/service/dashboard'
    },
    {
        path: '/daf',
        redirect: '/daf/dashboard'
    },
    {
        path: '/compta',
        redirect: '/compta/dashboard'
    },
    {
        path: '/dg',
        redirect: '/dg/dashboard'
    },
    {
        path: '/produits',
        redirect: '/admin/produits'
    },
    {
        path: '/laboratoires',
        redirect: '/admin/laboratoires'
    },
    {
        path: '/utilisateurs',
        redirect: '/admin/utilisateurs'
    },
    {
        path: '/historique',
        redirect: '/admin/historique'
    },
    {
        path: '/rapports',
        redirect: '/admin/rapports'
    },
    {
        path: '/dashboard',
        redirect: function() {
            const authStore = useAuthStore()
            if (authStore.isAuthenticated) {
                return authStore.getDefaultDashboard()
            }
            return '/'
        }
    },
    {
        path: '/settings',
        redirect: function() {
            const authStore = useAuthStore()
            if (authStore.isAuthenticated) {
                return authStore.getDefaultDashboard().replace('/dashboard', '/parametres')
            }
            return '/'
        }
    },
    {
        path: '/config',
        redirect: '/settings'
    },
    {
        path: '/profile',
        redirect: function() {
            const authStore = useAuthStore()
            return authStore.isAuthenticated ? authStore.getDefaultDashboard().replace('/dashboard', '/profile') : '/'
        }
    },
    {
        path: '/change-password',
        redirect: function() {
            const authStore = useAuthStore()
            return authStore.isAuthenticated ? authStore.getDefaultDashboard().replace('/dashboard', '/change-password') : '/'
        }
    },

    // Route 404
    {
        path: '/:pathMatch(.*)*',
        redirect: '/'
    }
]

const router = createRouter({
    history: createWebHistory(),
    routes,
})

// Middleware d'authentification
router.beforeEach(function(to, from, next) {
    const authStore = useAuthStore()

    // Loader — protégé car Pinia peut ne pas être prêt au 1er chargement
    try {
        const loaderStore = useLoaderStore()
        if (from.name !== to.name) {
            loaderStore.show('Chargement...')
        }
    } catch (e) {
        /* Pinia pas encore prêt */
    }

    // Réhydrater le store depuis localStorage à chaque navigation
    // (important au refresh de page)
    if (!authStore.user && authStore.token) {
        authStore.initializeStore()
    }

    if (to.meta.public) {
        if (authStore.isAuthenticated) {
            next(authStore.getDefaultDashboard())
            return
        }
        next()
        return
    }

    if (!authStore.isAuthenticated && to.meta.requiresAuth) {
        next('/')
        return
    }

    if (authStore.isAuthenticated && !authStore.isSessionValid()) {
        next('/')
        return
    }

    if (to.meta.roles && !to.meta.roles.includes(authStore.userRole)) {
        next(authStore.getDefaultDashboard())
        return
    }

    if (authStore.isAuthenticated && !to.meta.roles && !authStore.isRouteAllowed(to.path)) {
        next(authStore.getDefaultDashboard())
        return
    }

    if (to.meta.requiresPermission || to.meta.permission) {
        const requiredPermission = to.meta.requiresPermission || to.meta.permission
        if (authStore.userRole === 'ADMIN') {
            next();
            return
        }
        const userPermissions = JSON.parse(localStorage.getItem('userPermissions') || '[]')
        if (!userPermissions.includes(requiredPermission)) {
            next(authStore.getDefaultDashboard())
            return
        }
    }

    next()
})

// Middleware titre de page
router.afterEach(function(to) {
    const baseTitle = 'LabStock Pro'
    document.title = to.meta.title ? (to.meta.title + ' - ' + baseTitle) : baseTitle
    const authStore = useAuthStore()
    if (authStore.isAuthenticated) {
        authStore.renewSession()
    }
    // Cacher le loader après la navigation
    try {
        useLoaderStore().hide()
    } catch (e) {
        /* Pinia pas encore prêt */
    }
})

export default router